// Configuração da aba Diário (0.65): como o diário abre, quando pedir a senha de novo, código de recuperação,
// lembrete diário, sequência dos hábitos, aviso da revisão do mês, clima automático e páginas desta aba.
// 0.66 (D): tudo encolhido. Liga/desliga = linha com interruptor; escolha = linha com o valor atual e › (abre as opções, ✓ na atual).
// Aparece em Ajustes › Abas › Diário e no ⚙️ da página inicial do Diário.
import { useState, type ReactNode } from 'react';
import { Alert, Platform, Text, View } from 'react-native';
import { useDoc } from '../../store/store';
import {
  abrirPeloAparelho, confDiario, diarioAberto, diarioConfigurado, mudarConf, mudarModoDiario, novoCodigoRecuperacao, temChaveNoAparelho,
  temSenhaDiario, tipoSenhaDiario, trocarSenhaDiario, useDiario, type RegraRepetir,
} from '../../store/diario';
import { useConfirmarIdentidade } from '../ConfirmarIdentidade';
import { ActionSheet, Sheet, type Action } from '../ui';
import { C } from '../theme';
import { PickerModal } from '../Pickers';
import { PaginasDaAba } from '../ConfigPaginas';
import { CampoSenhaDiario, CodigoRecuperacao, CriarSenhaDiario } from './Senha';
import { useConfigPaginas } from '../../lib/paginas';
import { Divisor, LinhaChave, LinhaEscolha } from '../ajustes/Linhas';
import { t } from '../../lib/i18n';
import { horaTela } from '../../lib/date';
import { AlertasEditor } from '../AlertasEditor';
import { alertasDoLembrete, lembreteDosAlertas } from '../../store/diario';

const REGRAS: [RegraRepetir, string][] = [['sair', 'Toda vez que sair do Diário'], ['5min', 'Depois de 5 minutos fora'], ['app', 'Só quando o app bloquear']];
const CURTO: Record<RegraRepetir, string> = { sair: 'Toda vez que sair', '5min': 'Depois de 5 min fora', app: 'Quando o app bloquear' };

// LinhaEscolha, LinhaChave e Divisor: as mesmas de todas as páginas de Ajustes (0.67).

export function ConfigDiario({ cor }: { cor: string }) {
  useDiario();
  const dp = useDoc('diariop');
  const conf = confDiario();
  const ident = useConfirmarIdentidade(cor);
  const pag = useConfigPaginas('diario');
  const [hora, setHora] = useState(false);
  const [menu, setMenu] = useState<{ t: string; a: Action[] } | null>(null);
  const [folha, setFolha] = useState<null | 'senhaNova' | 'trocar' | 'paginas'>(null);
  const [codigo, setCodigo] = useState<string | null>(null);
  const [s0, setS0] = useState('');
  const modo = dp.modo ?? 'propria';
  const ok = diarioConfigurado();
  const marca = (on: boolean, l: string) => (on ? '✓ ' : '    ') + l;

  /** O diário precisa estar aberto (a chave na memória) para mudar a forma de abrir, a senha ou o código. */
  async function precisaAberto(motivo: string) {
    if (diarioAberto()) return true;
    if (modo === 'app' && Platform.OS !== 'web' && (await temChaveNoAparelho())) {
      if (!(await ident.pedir(motivo))) return false;
      if (await abrirPeloAparelho()) return true;
    }
    Alert.alert(t('Abra o diário primeiro'), t('Entre no Diário (com a senha dele ou o desbloqueio do app) e volte aqui.'));
    return false;
  }

  const comoAbre = () => setMenu({ t: t('Como o diário abre'), a: [
    ...(Platform.OS !== 'web' ? [{ label: marca(modo === 'app', t('A mesma do app e do celular')), onPress: async () => {
      if (modo === 'app' || !(await precisaAberto(t('Mudar como o diário abre')))) return;
      await mudarModoDiario('app');
    } }] : []),
    { label: marca(modo === 'propria', t('Uma senha própria, só do diário')), onPress: async () => {
      if (modo === 'propria' || !(await precisaAberto(t('Mudar como o diário abre')))) return;
      if (temSenhaDiario()) await mudarModoDiario('propria'); else setFolha('senhaNova');
    } },
    ...(modo === 'propria' && temSenhaDiario() ? [{ label: '🔑 ' + t('Mudar senha do diário'), onPress: () => { setS0(''); setFolha('trocar'); } }] : []),
  ] });
  const repetir = () => setMenu({ t: t('Pedir a senha de novo'), a: REGRAS.map(([k, l]) => ({ label: marca(conf.repetir === k, t(l)), onPress: () => mudarConf({ repetir: k }) })) });
  const sequencia = () => setMenu({ t: t('Sequência dos hábitos'), a: [
    { label: marca(conf.seq !== 'mes', t('N dias seguidos')), onPress: () => mudarConf({ seq: 'seguidos' }) },
    { label: marca(conf.seq === 'mes', t('N dias cumpridos no mês')), onPress: () => mudarConf({ seq: 'mes' }) },
  ] });
  async function codigoNovo() {
    if (!(await ident.pedir(t('Código de recuperação')))) return;
    if (!(await precisaAberto(t('Código de recuperação')))) return;
    Alert.alert(t('Gerar um código de recuperação novo?'), t('O código anterior deixa de valer. Por segurança, o código não fica guardado no app: só dá para ver na hora em que é gerado.'), [
      { text: t('Cancelar'), style: 'cancel' },
      { text: t('Gerar novo código'), onPress: async () => { const c = await novoCodigoRecuperacao(); if (c) setCodigo(c); } }]);
  }

  const bloco = (filhos: ReactNode) => <View style={{ backgroundColor: C.card, borderRadius: 14, paddingHorizontal: 14, paddingVertical: 4 }}>{filhos}</View>;
  return (
    <View style={{ gap: 10 }}>
      {bloco(<>
        <LinhaEscolha icone="🔐" titulo={t('Como o diário abre')} valor={!ok ? t('Ainda não criado') : modo === 'app' ? t('A mesma do app') : t('Senha própria')}
          onPress={() => (ok ? comoAbre() : Alert.alert(t('Diário ainda não criado'), t('Entre na aba Diário para criar o seu diário.')))} />
        {/* 0.72 (texto 10 C9): com "A mesma do app" a linha só informa; "Mudar senha do diário" só existe com a senha própria */}
        {ok && modo === 'app' && (<><Divisor /><LinhaEscolha icone="🔑" titulo={t('Senha do diário')} valor={t('Usa a senha do app')} onPress={comoAbre} /></>)}
        {ok && modo === 'propria' && temSenhaDiario() && (<><Divisor /><LinhaEscolha icone="🔑" titulo={t('Mudar senha do diário')} onPress={() => { setS0(''); setFolha('trocar'); }} /></>)}
        <Divisor />
        <LinhaEscolha icone="🔁" titulo={t('Pedir a senha de novo')} valor={t(CURTO[conf.repetir])} onPress={repetir} />
        <Divisor />
        <LinhaEscolha icone="🔑" titulo={t('Código de recuperação')} onPress={() => (ok ? codigoNovo() : Alert.alert(t('Diário ainda não criado'), t('Entre na aba Diário para criar o seu diário.')))}
          dica={t('Para recuperar o diário se esquecer a senha ou trocar de dispositivo.')} />
      </>)}
      {bloco(<>
        <LinhaChave icone="🔔" titulo={t('Lembrete diário')} cor={cor} valor={conf.lembrete.on} onMudar={v => mudarConf({ lembrete: { ...conf.lembrete, on: v } })}
          dica={t('Aviso sem nenhum conteúdo do diário: "Como foi o seu dia? ✍️"')} />
        {/* texto 10 (A4): o horário fica no mesmo editor de alertas da Nova tarefa ("Como avisar", "Testar" e outros horários) */}
        {conf.lembrete.on && (
          <View style={{ paddingHorizontal: 14, paddingVertical: 10 }}>
            <AlertasEditor itens={alertasDoLembrete(conf.lembrete)} temHorario={false} horaPadrao={conf.lembrete.hora} cor={cor} aba="diario"
              onChange={l => mudarConf({ lembrete: lembreteDosAlertas(conf.lembrete, l) })} />
          </View>
        )}
        <Divisor />
        <LinhaEscolha icone="🔥" titulo={t('Sequência dos hábitos')} valor={conf.seq === 'mes' ? t('Dias no mês') : t('Dias seguidos')} onPress={sequencia} />
        <Divisor />
        <LinhaChave icone="🗓️" titulo={t('Aviso da revisão do mês')} cor={cor} valor={conf.revisao} onMudar={v => mudarConf({ revisao: v })} />
        <Divisor />
        <LinhaChave icone="🌤️" titulo={t('Clima automático')} cor={cor} valor={conf.clima} onMudar={v => mudarConf({ clima: v })} />
        <Divisor />
        <LinhaEscolha icone="📄" titulo={t('Páginas desta aba')} valor={pag.personalizada ? t('Personalizada') : t('Padrão do app')} onPress={() => setFolha('paginas')} />
      </>)}

      <ActionSheet title={menu?.t ?? ''} actions={menu?.a ?? null} onClose={() => setMenu(null)} />
      {hora && <PickerModal mode="time" cor={cor} value={(() => { const d = new Date(); const [h, m] = conf.lembrete.hora.split(':').map(Number); d.setHours(h, m, 0, 0); return d; })()}
        onClose={() => setHora(false)} onPick={d => mudarConf({ lembrete: { ...conf.lembrete, on: true, hora: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` } })} />}
      <Sheet visible={folha === 'paginas'} onClose={() => setFolha(null)} title={t('Páginas desta aba')}>
        <View style={{ padding: 16 }}><PaginasDaAba aba="diario" cor={cor} /></View>
      </Sheet>
      <Sheet visible={folha === 'senhaNova'} onClose={() => setFolha(null)} title={t('Senha própria do diário')}>
        <View style={{ padding: 16, gap: 10 }}>
          <Text style={{ color: C.sub }}>{t('Crie a senha que vai abrir o diário.')}</Text>
          <CriarSenhaDiario cor={cor} botao={t('Salvar')} onPronta={async (sn, tp) => { if (await mudarModoDiario('propria', sn, tp)) { setFolha(null); Alert.alert(t('Pronto'), t('O diário agora abre só com a senha dele.')); } }} />
        </View>
      </Sheet>
      <Sheet visible={folha === 'trocar'} onClose={() => setFolha(null)} title={t('Mudar senha do diário')}>
        <View style={{ padding: 16, gap: 10 }}>
          <CampoSenhaDiario tipo={tipoSenhaDiario()} cor={cor} valor={s0} onValor={setS0} ph={t('Senha atual do diário')} />
          {!!s0 && (
            <CriarSenhaDiario cor={cor} botao={t('Trocar')} onPronta={async (sn, tp) => {
              try {
                if (await trocarSenhaDiario(s0, sn, tp)) { Alert.alert(t('Senha do diário trocada')); setS0(''); setFolha(null); }
                else { setS0(''); Alert.alert(t('Senha do diário incorreta.')); }
              } catch { Alert.alert(t('Não foi possível')); }
            }} />
          )}
        </View>
      </Sheet>
      <Sheet visible={!!codigo} onClose={() => setCodigo(null)} title={t('Código de recuperação')}>
        {!!codigo && <CodigoRecuperacao embutido codigo={codigo} cor={cor} onPronto={() => setCodigo(null)} />}
      </Sheet>
      {ident.tela}
    </View>
  );
}
